import React from 'react';
import styled, { useTheme } from 'styled-components';
import type {
  CompletenessStatus,
  OverallCompleteness,
  ProfileSectionId,
} from '../../utils/profileSectionCompleteness';
import SectionCompletenessBadge from './SectionCompletenessBadge';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { typography } from '../../design-system/tokens/typography';
import { animations } from '../../design-system/tokens/animations';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { hexToRgba } from '../../utils/colorUtils';
import type { ThemeColors } from '../../assets/theme';

export interface ProfileCompletenessSummaryProps {
  overall: OverallCompleteness;
  title: string;
  hint: string;
  statusLabel: string;
  sectionLabels: Record<ProfileSectionId, string>;
  getStatusLabel: (status: CompletenessStatus) => string;
  onJumpToSection?: (sectionId: ProfileSectionId) => void;
}

const SECTION_ORDER: ProfileSectionId[] = [
  'personal',
  'activities',
  'work',
  'studies',
  'committees',
];

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[4]} ${spacing[5]};
  margin-bottom: ${spacing[5]};
  border-radius: ${borders.radius.lg};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.surface} 0%,
    ${props => props.theme.colors.icomLightBlue || hexToRgba(props.theme.colors.primary, 0.06)} 100%
  );

  ${breakpoints.down.md} {
    padding: ${spacing[4]};
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  line-height: ${typography.lineHeight.tight};
`;

const Hint = styled(Text)`
  margin-top: ${spacing[1]};
  display: block;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: ${borders.radius.full};
  background: ${props => props.theme.colors.icomWarmGray || props.theme.colors.surfaceHover};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${props => props.$pct}%;
  background: ${props => props.$color};
  border-radius: ${borders.radius.full};
  transition: width ${animations.duration.normal} ${animations.easing.easeOut};
`;

const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${spacing[2]};

  ${breakpoints.down.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${breakpoints.down.sm} {
    grid-template-columns: 1fr;
  }
`;

const MiniCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  align-items: flex-start;
  text-align: left;
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borders.radius.md};
  border: ${borders.width.thin} solid
    ${props =>
      props.$active
        ? props.theme.colors.primary
        : props.theme.colors.borderLight || props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  font-family: ${typography.fontFamily.primary};
  transition: border-color ${animations.duration.fast} ${animations.easing.easeOut},
    box-shadow ${animations.duration.fast} ${animations.easing.easeOut};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const MiniLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${typography.lineHeight.tight};
`;

const MiniPct = styled.span<{ $color: string }>`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
`;

function statusColor(status: CompletenessStatus, colors: ThemeColors): string {
  switch (status) {
    case 'complete':
      return colors.success;
    case 'partial':
      return colors.icomGold || colors.accent || colors.warning;
    case 'notApplicable':
      return colors.textMuted || colors.textSecondary;
    default:
      return colors.error;
  }
}

export const ProfileCompletenessSummary: React.FC<ProfileCompletenessSummaryProps> = ({
  overall,
  title,
  hint,
  statusLabel,
  sectionLabels,
  getStatusLabel,
  onJumpToSection,
}) => {
  const theme = useTheme();
  const barColor = statusColor(overall.status, theme.colors);

  return (
    <Shell>
      <TopRow>
        <TitleBlock>
          <Title>{title}</Title>
          <Hint size="sm" color="secondary">
            {hint}
          </Hint>
        </TitleBlock>
        <SectionCompletenessBadge
          percentage={overall.percentage}
          status={overall.status}
          statusLabel={statusLabel}
          size="md"
          ariaLabel={title}
        />
      </TopRow>

      <Track
        role="progressbar"
        aria-valuenow={overall.percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={title}
      >
        <Fill $pct={overall.percentage} $color={barColor} />
      </Track>

      <MiniGrid>
        {SECTION_ORDER.map(id => {
          const section = overall.sections[id];
          const color = statusColor(section.status, theme.colors);
          const pctText =
            section.status === 'notApplicable'
              ? getStatusLabel('notApplicable')
              : `${section.percentage}%`;

          return (
            <MiniCard
              key={id}
              type="button"
              $active={false}
              onClick={
                onJumpToSection
                  ? () => onJumpToSection(id)
                  : undefined
              }
            >
              <MiniLabel>{sectionLabels[id]}</MiniLabel>
              <MiniPct $color={color}>{pctText}</MiniPct>
            </MiniCard>
          );
        })}
      </MiniGrid>
    </Shell>
  );
};

export default ProfileCompletenessSummary;
